import React, { useState, useEffect, useCallback } from 'react';
import {
  Container, Typography, Box, Button, Card, CardHeader, CardContent,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Divider, Alert, CircularProgress, DialogTitle, DialogContent,
  DialogActions, TextField, IconButton, Tooltip, Grid,
  Select, MenuItem, FormControl, InputLabel, Chip,
} from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import heLocale from 'date-fns/locale/he';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import API from '../services/api';
import { useUser } from '../contexts/UserContext';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { generateBusinessHeader, openPrintWindow } from '../utils/printUtils';
import MobileDialog from './dialogs/MobileDialog';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import PrintIcon from '@mui/icons-material/Print';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import DescriptionIcon from '@mui/icons-material/Description';
import DownloadIcon from '@mui/icons-material/Download';
import LinkIcon from '@mui/icons-material/Link';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InventoryIcon from '@mui/icons-material/Inventory';
import HomeIcon from '@mui/icons-material/Home';

const theme = createTheme({ direction: 'rtl' });

const QuotationDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { userProfile } = useUser();

  const uiConfig = userProfile?.ui_config || {};
  const generalSchema = uiConfig?.orders_general_configs?.general_schema || {};
  const productLabel = generalSchema?.product?.label || 'מוצר';
  const productPluralLabel = generalSchema?.product?.plural_label || 'מוצרים';

  const orderConfig = userProfile?.group?.order_config || {};
  const storageConfig = orderConfig.storage_config || {};
  const storageEnabled = storageConfig.enabled && (uiConfig.allow_order_management || false);

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [convertDialogOpen, setConvertDialogOpen] = useState(false);
  const [convertOrderDate, setConvertOrderDate] = useState(new Date());
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isConverting, setIsConverting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [editingNotes, setEditingNotes] = useState(false);
  const [notesValue, setNotesValue] = useState('');

  // Storage/inventory state for conversion
  const [inventoryData, setInventoryData] = useState(null);
  const [isLoadingInventory, setIsLoadingInventory] = useState(false);
  const [storageSelections, setStorageSelections] = useState({});
  const [allowNegativeInventory, setAllowNegativeInventory] = useState(false);

  // Fetch quotation
  const { data: quotation, isLoading, refetch } = useQuery({
    queryKey: ['quotation', id],
    queryFn: async () => {
      const { data } = await API.get('/quotation/', { params: { QuotationID: id } });
      return data;
    },
    staleTime: 30 * 1000,
  });

  const isConverted = quotation?.Status === 'converted';

  // Auto-open convert dialog when navigated from list page with openConvert state
  useEffect(() => {
    if (location.state?.openConvert && quotation && !isConverted) {
      setConvertDialogOpen(true);
      // Clear the state so it doesn't re-open on refresh
      navigate(location.pathname, { replace: true });
    }
  }, [location.state, quotation, isConverted, navigate, location.pathname]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('he-IL', { style: 'currency', currency: 'ILS' }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try { return format(new Date(dateStr), 'dd/MM/yyyy'); } catch { return dateStr; }
  };

  const businessProfile = userProfile?.group?.business_profile || {};

  const buildQuotationHtml = (mode) => {
    // mode: 'print' = auto-print, 'pdf' = show save-as-PDF instruction
    const items = quotation.items || [];
    const itemsRows = items.map((item, i) => `
      <tr>
        <td>${i + 1}</td>
        <td>${item.ProductName}</td>
        <td>${item.Quantity}</td>
        <td>${formatCurrency(item.UnitPrice)}</td>
        <td>${formatCurrency(item.Subtotal)}</td>
      </tr>
    `).join('');

    let totalsHtml = `
      <div class="totals-row"><span class="label">סה"כ לפני הנחה:</span><span>${formatCurrency(quotation.Subtotal)}</span></div>
    `;
    if (parseFloat(quotation.DiscountAmount) > 0 || parseFloat(quotation.DiscountPercentage) > 0) {
      totalsHtml += `<div class="totals-row"><span class="label">הנחה${parseFloat(quotation.DiscountPercentage) > 0 ? ` (${quotation.DiscountPercentage}%)` : ''}:</span><span style="color:red">-${formatCurrency(quotation.DiscountAmount)}</span></div>`;
    }
    if (quotation.IsTaxable) {
      totalsHtml += `
        <div class="totals-row"><span class="label">סכום לפני מע"מ:</span><span>${formatCurrency(quotation.NetAmount)}</span></div>
        <div class="totals-row"><span class="label">מע"מ (${quotation.TaxRate}%):</span><span>${formatCurrency(quotation.TaxAmount)}</span></div>
      `;
    }
    if (quotation.RoundingEnabled && parseFloat(quotation.RoundingAdjustment) !== 0) {
      totalsHtml += `<div class="totals-row"><span class="label">עיגול:</span><span>${formatCurrency(quotation.RoundingAdjustment)}</span></div>`;
    }
    totalsHtml += `<div class="totals-row total"><span>סה"כ:</span><span>${formatCurrency(quotation.TotalAmount)}</span></div>`;

    const toolbarText = mode === 'pdf'
      ? '<b>לשמירה כ-PDF:</b> בחלון ההדפסה בחר "שמור כ-PDF" כיעד ולחץ שמור'
      : '';

    return `
      <!DOCTYPE html>
      <html dir="rtl">
      <head>
        <title>הצעת מחיר ${quotation.QuotationNumber}</title>
        <meta charset="utf-8" />
        <style>
          body { font-family: Arial, sans-serif; margin: 20px; direction: rtl; font-size: 12px; color: #333; }
          h1 { text-align: center; font-size: 22px; margin: 15px 0 5px; }
          .subtitle { text-align: center; color: #666; font-size: 14px; margin-bottom: 20px; }
          .meta-section { display: flex; justify-content: space-between; margin-bottom: 20px; gap: 20px; }
          .meta-box { flex: 1; background: #f8f9fa; border: 1px solid #e0e0e0; border-radius: 4px; padding: 10px 12px; }
          .meta-box h3 { margin: 0 0 6px; font-size: 13px; border-bottom: 1px solid #ddd; padding-bottom: 4px; }
          .meta-box p { margin: 3px 0; font-size: 12px; }
          .label { color: #666; }
          table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
          th { background: #4a5568; color: white; padding: 8px 10px; text-align: right; font-size: 12px; }
          td { padding: 7px 10px; text-align: right; border-bottom: 1px solid #e0e0e0; }
          tr:nth-child(even) { background: #f8f9fa; }
          .totals-section { width: auto; max-width: 300px; margin-right: auto; margin-left: 0; }
          .totals-row { display: flex; justify-content: space-between; padding: 4px 0; font-size: 12px; }
          .totals-row.total { font-weight: bold; font-size: 14px; border-top: 2px solid #333; padding-top: 8px; margin-top: 4px; }
          .notes { margin-top: 20px; padding: 10px; background: #f8f9fa; border: 1px solid #e0e0e0; border-radius: 4px; }
          .notes h3 { margin: 0 0 6px; font-size: 13px; }
          .validity { text-align: center; margin-top: 20px; padding: 8px; background: #fff3cd; border: 1px solid #ffc107; border-radius: 4px; color: #856404; }
          .footer { margin-top: 30px; text-align: center; font-size: 10px; color: #999; border-top: 1px solid #e0e0e0; padding-top: 10px; }
          .toolbar { text-align: center; margin-bottom: 10px; padding: 8px; background: #e3f2fd; border-radius: 4px; }
          .toolbar button { padding: 6px 20px; cursor: pointer; margin: 0 5px; }
          @media print { .toolbar { display: none; } }
        </style>
      </head>
      <body>
        ${generateBusinessHeader(businessProfile)}
        <div class="toolbar">
          ${toolbarText ? `<span style="font-size:13px;">${toolbarText}</span><br/><br/>` : ''}
          <button onclick="window.print()">הדפס</button>
          <button onclick="window.close()">סגור</button>
        </div>
        <h1>הצעת מחיר</h1>
        <div class="subtitle">מס' ${quotation.QuotationNumber}</div>
        <div class="meta-section">
          <div class="meta-box">
            <h3>פרטי לקוח</h3>
            <p><span class="label">שם:</span> ${quotation.ClientName}</p>
            <p><span class="label">ח.פ/ת.ז:</span> ${quotation.ClientBusinessID}</p>
          </div>
          <div class="meta-box">
            <h3>פרטי הצעה</h3>
            <p><span class="label">תאריך:</span> ${formatDate(quotation.QuotationDate)}</p>
            ${quotation.ValidUntil ? `<p><span class="label">תוקף עד:</span> ${formatDate(quotation.ValidUntil)}</p>` : ''}
          </div>
        </div>
        <table>
          <thead><tr><th>#</th><th>${productLabel}</th><th>כמות</th><th>מחיר יחידה</th><th>סה"כ</th></tr></thead>
          <tbody>${itemsRows}</tbody>
        </table>
        <div class="totals-section">${totalsHtml}</div>
        ${quotation.Notes ? `<div class="notes"><h3>הערות</h3><p>${quotation.Notes}</p></div>` : ''}
        ${quotation.ValidUntil ? `<div class="validity">הצעת מחיר זו בתוקף עד תאריך ${formatDate(quotation.ValidUntil)}</div>` : ''}
        <div class="footer">הופק בתאריך ${format(new Date(), 'dd/MM/yyyy HH:mm')}</div>
      </body>
      </html>
    `;
  };

  const handlePrintQuotation = () => {
    openPrintWindow(buildQuotationHtml('print'));
  };

  const handleDownloadPDF = () => {
    openPrintWindow(buildQuotationHtml('pdf'));
  };

  const formatDateLocal = (date) => {
    if (!date) return null;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Fetch inventory data when conversion dialog opens (only if storage enabled)
  const fetchInventoryData = useCallback(async () => {
    if (!storageEnabled || !id) return;
    setIsLoadingInventory(true);
    try {
      const { data } = await API.get('/quotation/check-inventory/', { params: { QuotationID: id } });
      setInventoryData(data);
      setAllowNegativeInventory(data.allow_negative_inventory || false);

      // Auto-select default storage for each item
      const defaults = {};
      (data.items || []).forEach(item => {
        const defaultStorage = item.inventory.find(inv => inv.is_default);
        if (defaultStorage) {
          defaults[item.QuotationItemID] = defaultStorage.StorageID;
        } else if (item.inventory.length > 0) {
          defaults[item.QuotationItemID] = item.inventory[0].StorageID;
        }
      });
      setStorageSelections(defaults);
    } catch (err) {
      setError(err?.response?.data?.error || 'שגיאה בטעינת נתוני מלאי');
    } finally {
      setIsLoadingInventory(false);
    }
  }, [storageEnabled, id]);

  useEffect(() => {
    if (convertDialogOpen && storageEnabled) {
      fetchInventoryData();
    }
    if (!convertDialogOpen) {
      setInventoryData(null);
      setStorageSelections({});
      setIsConverting(false);
    }
  }, [convertDialogOpen, storageEnabled, fetchInventoryData]);

  // Check if any item has insufficient inventory in the selected storage
  const getItemInventoryStatus = useCallback((item) => {
    const selectedStorageId = storageSelections[item.QuotationItemID];
    if (!selectedStorageId) return { sufficient: false, available: 0 };
    const storageInv = item.inventory.find(inv => inv.StorageID === selectedStorageId);
    if (!storageInv) return { sufficient: false, available: 0 };
    return {
      sufficient: storageInv.available_quantity >= item.Quantity,
      available: storageInv.available_quantity,
    };
  }, [storageSelections]);

  const hasInsufficientInventory = inventoryData?.items?.some(item => {
    const status = getItemInventoryStatus(item);
    return !status.sufficient;
  }) || false;

  const handleConvert = async () => {
    setIsConverting(true);
    setError('');
    try {
      const payload = { QuotationID: id };
      if (convertOrderDate) {
        payload.OrderDate = formatDateLocal(convertOrderDate);
      }
      // Include storage selections if storage is enabled
      if (storageEnabled && inventoryData?.storage_enabled) {
        payload.items = (inventoryData.items || []).map(item => ({
          QuotationItemID: item.QuotationItemID,
          StorageID: storageSelections[item.QuotationItemID],
        }));
      }
      const { data } = await API.post('/quotation/convert/', payload);
      setConvertDialogOpen(false);
      navigate(`/order/${data.OrderID}`, { state: { from: '/orders' } });
    } catch (err) {
      setError(err?.response?.data?.error || 'שגיאה בהמרה להזמנה');
      setIsConverting(false);
    }
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    setError('');
    try {
      await API.post('/quotation/delete/', { QuotationID: id });
      navigate('/quotations');
    } catch (err) {
      setError(err?.response?.data?.error || 'שגיאה במחיקה');
      setIsDeleting(false);
    }
  };

  const handleSaveNotes = async () => {
    try {
      await API.post('/quotation/update/', { QuotationID: id, Notes: notesValue });
      setEditingNotes(false);
      refetch();
    } catch (err) {
      setError(err?.response?.data?.error || 'שגיאה בעדכון הערות');
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!quotation) {
    return (
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Alert severity="error">הצעת המחיר לא נמצאה</Alert>
      </Container>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <Container maxWidth="lg" sx={{ py: 3 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="h5" fontWeight="bold">
              <DescriptionIcon sx={{ mr: 1, verticalAlign: 'middle', direction: 'ltr' }} />
              הצעת מחיר {quotation.QuotationNumber}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate('/quotations')} sx={{ direction: 'ltr' }}>
              חזרה
            </Button>
            <Button variant="outlined" startIcon={<PrintIcon />} onClick={handlePrintQuotation} sx={{ direction: 'ltr' }}>
              הדפס
            </Button>
            <Button variant="outlined" startIcon={<DownloadIcon />} onClick={handleDownloadPDF} sx={{ direction: 'ltr', whiteSpace: 'nowrap' }}>
              PDF
            </Button>
            {!isConverted && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<SwapHorizIcon />}
                onClick={() => setConvertDialogOpen(true)}
                sx={{ direction: 'ltr' }}
              >
                המר להזמנה
              </Button>
            )}
            {!isConverted && (
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={() => setDeleteDialogOpen(true)}
                sx={{ direction: 'ltr' }}
              >
                מחק
              </Button>
            )}
          </Box>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

        {/* Converted Order Link */}
        {isConverted && quotation.ConvertedToOrderID && (
          <Alert severity="info" sx={{ mb: 2 }} icon={<LinkIcon />}>
            הצעת מחיר זו הומרה להזמנה מספר{' '}
            <Typography
              component="span"
              sx={{ cursor: 'pointer', color: 'primary.main', textDecoration: 'underline', fontWeight: 'bold' }}
              onClick={() => navigate(`/order/${quotation.ConvertedToOrderID}`)}
            >
              {quotation.ConvertedToOrderNumber}
            </Typography>
          </Alert>
        )}

        {/* Details Cards */}
        <Grid container spacing={3} sx={{ mb: 3, alignItems: 'stretch' }}>
          {/* Client Info */}
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%' }}>
              <CardHeader title={<Typography variant="h6">פרטי לקוח</Typography>} />
              <CardContent>
                <Typography><strong>שם:</strong> {quotation.ClientName}</Typography>
                <Typography><strong>ח.פ/ת.ז:</strong> {quotation.ClientBusinessID}</Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Quotation Info */}
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%' }}>
              <CardHeader title={<Typography variant="h6">פרטי הצעה</Typography>} />
              <CardContent>
                <Typography><strong>תאריך:</strong> {formatDate(quotation.QuotationDate)}</Typography>
                <Typography><strong>תוקף עד:</strong> {formatDate(quotation.ValidUntil)}</Typography>
                <Typography><strong>נוצר:</strong> {formatDate(quotation.CreatedAt)}</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Items Table */}
        <Card sx={{ mb: 3 }}>
          <CardHeader title={<Typography variant="h6">{productPluralLabel}</Typography>} />
          <CardContent>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>#</TableCell>
                    <TableCell>{productLabel}</TableCell>
                    <TableCell>כמות</TableCell>
                    <TableCell>מחיר יחידה</TableCell>
                    <TableCell>סה"כ</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(quotation.items || []).map((item, index) => (
                    <TableRow key={item.QuotationItemID}>
                      <TableCell>{index + 1}</TableCell>
                      <TableCell>{item.ProductName}</TableCell>
                      <TableCell>{item.Quantity}</TableCell>
                      <TableCell>{formatCurrency(item.UnitPrice)}</TableCell>
                      <TableCell>{formatCurrency(item.Subtotal)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>

        {/* Totals */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ maxWidth: 350, mr: 'auto' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography color="text.secondary">סה"כ לפני הנחה:</Typography>
                <Typography>{formatCurrency(quotation.Subtotal)}</Typography>
              </Box>
              {(parseFloat(quotation.DiscountAmount) > 0 || parseFloat(quotation.DiscountPercentage) > 0) && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography color="text.secondary">
                    הנחה{parseFloat(quotation.DiscountPercentage) > 0 ? ` (${quotation.DiscountPercentage}%)` : ''}:
                  </Typography>
                  <Typography color="error">-{formatCurrency(quotation.DiscountAmount)}</Typography>
                </Box>
              )}
              {quotation.IsTaxable && (
                <>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography color="text.secondary">סכום לפני מע"מ:</Typography>
                    <Typography>{formatCurrency(quotation.NetAmount)}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography color="text.secondary">מע"מ ({quotation.TaxRate}%):</Typography>
                    <Typography>{formatCurrency(quotation.TaxAmount)}</Typography>
                  </Box>
                </>
              )}
              {quotation.RoundingEnabled && parseFloat(quotation.RoundingAdjustment) !== 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography color="text.secondary">עיגול:</Typography>
                  <Typography>{formatCurrency(quotation.RoundingAdjustment)}</Typography>
                </Box>
              )}
              <Divider sx={{ my: 1 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="h6" fontWeight="bold">סה"כ:</Typography>
                <Typography variant="h6" fontWeight="bold">{formatCurrency(quotation.TotalAmount)}</Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Notes */}
        <Card sx={{ mb: 3 }}>
          <CardHeader
            title={<Typography variant="h6">הערות</Typography>}
            action={
              !isConverted && !editingNotes && (
                <IconButton onClick={() => { setEditingNotes(true); setNotesValue(quotation.Notes || ''); }}>
                  <EditIcon />
                </IconButton>
              )
            }
          />
          <CardContent>
            {editingNotes ? (
              <Box>
                <TextField
                  fullWidth multiline rows={3}
                  value={notesValue}
                  onChange={(e) => setNotesValue(e.target.value)}
                />
                <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
                  <Button size="small" variant="contained" startIcon={<SaveIcon />} onClick={handleSaveNotes} sx={{ direction: 'ltr' }}>
                    שמור
                  </Button>
                  <Button size="small" variant="outlined" startIcon={<CancelIcon />} onClick={() => setEditingNotes(false)} sx={{ direction: 'ltr' }}>
                    ביטול
                  </Button>
                </Box>
              </Box>
            ) : (
              <Typography sx={{ whiteSpace: 'pre-wrap' }}>
                {quotation.Notes || 'אין הערות'}
              </Typography>
            )}
          </CardContent>
        </Card>

        {/* Convert Confirmation Dialog */}
        <MobileDialog
          open={convertDialogOpen}
          onClose={() => setConvertDialogOpen(false)}
          maxWidth={storageEnabled ? 'md' : 'sm'}
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <SwapHorizIcon />
              המרה להזמנה
            </Box>
          </DialogTitle>
          <DialogContent>
            <Typography>
              האם להמיר את הצעת מחיר {quotation.QuotationNumber} להזמנה חדשה?
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
              הזמנה חדשה תיווצר עם אותם פריטים ותנאים. הצעת המחיר תסומן כ"הומרה".
            </Typography>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={heLocale}>
              <DatePicker
                label="תאריך הזמנה"
                value={convertOrderDate}
                onChange={(val) => setConvertOrderDate(val)}
                format="dd/MM/yyyy"
                slotProps={{
                  textField: { fullWidth: true, size: 'small', sx: { '& .MuiInputBase-root': { direction: 'ltr' } } }
                }}
              />
            </LocalizationProvider>

            {/* Storage/Inventory Section */}
            {storageEnabled && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle1" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <InventoryIcon fontSize="small" />
                  בחירת מחסן ובדיקת מלאי
                </Typography>

                {isLoadingInventory ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                    <CircularProgress size={30} />
                    <Typography sx={{ mr: 2 }} color="text.secondary">טוען נתוני מלאי...</Typography>
                  </Box>
                ) : inventoryData?.items?.length > 0 ? (
                  <>
                    {/* Warnings */}
                    {hasInsufficientInventory && !allowNegativeInventory && (
                      <Alert severity="error" sx={{ mb: 2 }}>
                        <strong>לא ניתן להמיר:</strong> חלק מהפריטים חורגים מהמלאי הזמין. יש לעדכן את המלאי או לשנות מחסן.
                      </Alert>
                    )}
                    {hasInsufficientInventory && allowNegativeInventory && (
                      <Alert severity="warning" sx={{ mb: 2 }}>
                        <strong>שים לב:</strong> חלק מהפריטים חורגים מהמלאי הזמין. המלאי יירד מתחת לאפס.
                      </Alert>
                    )}

                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>{productLabel}</TableCell>
                            <TableCell>כמות</TableCell>
                            <TableCell>מחסן</TableCell>
                            <TableCell>מלאי זמין</TableCell>
                            <TableCell align="center">סטטוס</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {inventoryData.items.map((item) => {
                            const status = getItemInventoryStatus(item);
                            return (
                              <TableRow key={item.QuotationItemID}>
                                <TableCell>{item.ProductName}</TableCell>
                                <TableCell>{item.Quantity}</TableCell>
                                <TableCell sx={{ minWidth: 180 }}>
                                  <FormControl fullWidth size="small">
                                    <Select
                                      value={storageSelections[item.QuotationItemID] || ''}
                                      onChange={(e) => {
                                        setStorageSelections(prev => ({
                                          ...prev,
                                          [item.QuotationItemID]: e.target.value,
                                        }));
                                      }}
                                    >
                                      {item.inventory.map(inv => (
                                        <MenuItem key={inv.StorageID} value={inv.StorageID}>
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', justifyContent: 'space-between' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                              {inv.is_default && <HomeIcon sx={{ fontSize: 14 }} />}
                                              <Typography variant="body2">{inv.StorageName}</Typography>
                                            </Box>
                                            <Chip
                                              label={`${inv.available_quantity}`}
                                              size="small"
                                              color={inv.available_quantity >= item.Quantity ? 'success' : 'error'}
                                              variant="outlined"
                                              sx={{ minWidth: 40 }}
                                            />
                                          </Box>
                                        </MenuItem>
                                      ))}
                                    </Select>
                                  </FormControl>
                                </TableCell>
                                <TableCell>
                                  <Typography
                                    variant="body2"
                                    color={status.sufficient ? 'success.main' : 'error.main'}
                                    fontWeight="bold"
                                  >
                                    {status.available}
                                  </Typography>
                                </TableCell>
                                <TableCell align="center">
                                  {status.sufficient ? (
                                    <CheckCircleIcon color="success" fontSize="small" />
                                  ) : (
                                    <Tooltip title={`חסרים ${(item.Quantity - status.available).toFixed(2)} יחידות`}>
                                      <WarningAmberIcon color="error" fontSize="small" />
                                    </Tooltip>
                                  )}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </>
                ) : inventoryData && inventoryData.items?.length === 0 ? (
                  <Alert severity="info">אין פריטים בהצעת מחיר זו.</Alert>
                ) : null}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConvertDialogOpen(false)} disabled={isConverting}>ביטול</Button>
            <Button
              variant="contained"
              onClick={handleConvert}
              disabled={
                isConverting ||
                isLoadingInventory ||
                (storageEnabled && hasInsufficientInventory && !allowNegativeInventory)
              }
              startIcon={isConverting ? <CircularProgress size={20} /> : <SwapHorizIcon />}
              sx={{ direction: 'ltr' }}
            >
              {isConverting ? 'ממיר...' : 'המר להזמנה'}
            </Button>
          </DialogActions>
        </MobileDialog>

        {/* Delete Confirmation Dialog */}
        <MobileDialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
          <DialogTitle>מחיקת הצעת מחיר</DialogTitle>
          <DialogContent>
            <Typography>
              האם למחוק את הצעת מחיר {quotation.QuotationNumber}?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)} disabled={isDeleting}>ביטול</Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleDelete}
              disabled={isDeleting}
              startIcon={isDeleting ? <CircularProgress size={20} /> : <DeleteIcon />}
              sx={{ direction: 'ltr' }}
            >
              {isDeleting ? 'מוחק...' : 'מחק'}
            </Button>
          </DialogActions>
        </MobileDialog>
      </Container>
    </ThemeProvider>
  );
};

export default QuotationDetailPage;
